
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthUser, AppRole, Organization } from '@/types/auth';
import { withTimeout } from '@/utils/timeout';
import { authStateCache } from '@/utils/authStateCache';
import { activeOrgManager } from '@/utils/activeOrgManager';

export const userDataService = {
  async fetchUserData(authUser: User, hasExistingUser: boolean = false): Promise<AuthUser | null> {
    try {
      // Wrap the entire operation with retry logic and timeout
      return await withTimeout(
        this.fetchUserDataWithRetry(authUser),
        10000, // 10 second timeout
        'User data fetch timed out after 10 seconds'
      );
    } catch (error) {
      console.error('[UserDataService] Fetch failed:', error);
      // If we already have a user, throw error instead of returning fallback
      // This prevents overwriting valid user with fallback
      if (hasExistingUser) {
        throw error;
      }
      // Return a fallback minimal user object to prevent blocking login
      return this.createFallbackUser(authUser);
    }
  },

  async fetchUserDataWithRetry(authUser: User): Promise<AuthUser | null> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        return await this.fetchUserDataInternal(authUser);
      } catch (error) {
        lastError = error as Error;
        
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }
    
    throw lastError || new Error('Failed to fetch user data after 2 attempts');
  },

  async fetchUserDataInternal(authUser: User): Promise<AuthUser | null> {
    const startTime = performance.now();
    console.log('[Auth Performance] Starting single RPC auth snapshot');
    
    // Single RPC call to fetch all auth data at once
    const { data: snapshot, error: snapshotError } = await supabase.rpc('get_my_auth_snapshot');

    if (snapshotError) {
      console.error('[UserDataService] Auth snapshot RPC error:', snapshotError);
      throw snapshotError;
    }

    // Type the snapshot response
    const typedSnapshot = snapshot as {
      success: boolean;
      error?: string;
      profile?: any;
      roles?: Array<{ role: AppRole; is_organization_admin: boolean }>;
      organization?: any;
    };

    if (!typedSnapshot || !typedSnapshot.success) {
      console.error('[UserDataService] Auth snapshot failed:', typedSnapshot?.error);
      throw new Error(typedSnapshot?.error || 'Auth snapshot failed');
    }

    const { profile, roles: rolesData, organization: orgData } = typedSnapshot;

    if (!profile) {
      console.error('[UserDataService] No profile found in snapshot');
      throw new Error('No profile found');
    }

    console.log('[Auth] Auth snapshot received - Profile:', profile.email);
    console.log('[Auth] Super-admin status:', profile.is_super_admin);

    // Extract roles from snapshot
    let userRoles = (rolesData || []).map((r: any) => ({ 
      role: r.role as AppRole, 
      is_organization_admin: r.is_organization_admin 
    }));

    // Validate roles data - never proceed without roles
    if (!userRoles || userRoles.length === 0) {
      // Try to use cached auth state before failing
      const cachedUser = authStateCache.getSnapshot();
      if (cachedUser?.roles && cachedUser.roles.length > 0) {
        console.warn('[UserDataService] No roles from RPC, using cached user roles');
        userRoles = cachedUser.roles.map(role => ({ 
          role, 
          is_organization_admin: cachedUser.isOrganizationAdmin || false
        }));
      } else {
        throw new Error('No roles found for user and no valid cache - cannot proceed');
      }
    }

    const roles = userRoles.map(ur => ur.role);
    const isOrganizationAdmin = userRoles.some(ur => ur.is_organization_admin);

    console.log('[UserDataService] Roles:', roles, 'isOrgAdmin:', isOrganizationAdmin);

    // Handle organization data from snapshot
    const organization = orgData as Organization | undefined;
    if (organization) {
      console.log('[UserDataService] Organization:', organization.name);
    }

    const rolePriorities = { viewer: 1, cook: 2, manager: 3, admin: 4 };
    const highestRole = roles.reduce((highest, role) => 
      rolePriorities[role] > rolePriorities[highest] ? role : highest, 
      roles[0] || 'viewer'
    );

    const userData: AuthUser = {
      id: authUser.id,
      email: authUser.email!,
      profile: {
        ...profile,
        onboarding_progress: profile.onboarding_progress as any
      },
      roles,
      highestRole,
      rolePriority: rolePriorities[highestRole],
      isOrganizationAdmin,
      organization,
      isDemo: profile.is_demo || false
    };

    // Set active org in JWT for faster RLS checks
    if (organization?.id) {
      const currentOrgId = await activeOrgManager.getCurrentOrgId();
      if (currentOrgId !== organization.id) {
        console.log('[Auth] Setting active org in JWT:', organization.id);
        await activeOrgManager.setActiveOrg(organization.id);
      }
    }

    const endTime = performance.now();
    console.log(`[Auth Performance] Single RPC fetch time: ${(endTime - startTime).toFixed(0)}ms`);

    return userData;
  },

  async createFallbackUser(authUser: User): Promise<AuthUser> {
    console.log('[Auth] Creating fallback user for:', authUser.email);
    
    // Try to fetch basic profile data AND roles even in fallback mode
    let organizationId = null;
    let organizationName = null;
    let userRoles: AppRole[] | null = null; // Don't default to viewer yet
    let isOrgAdmin = false;
    let isDemo = false;
    let isSuperAdmin = false;
    
    try {
      // Fetch profile and roles in parallel with timeout
      const profilePromise = supabase
        .from('profiles')
        .select('organization_id, organization_name, is_super_admin, is_demo')
        .eq('id', authUser.id)
        .maybeSingle();
      
      const rolesPromise = supabase
        .from('user_roles')
        .select('role, is_organization_admin')
        .eq('user_id', authUser.id);
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 3000)
      );
      
      const [profileResult, rolesResult] = await Promise.race([
        Promise.all([profilePromise, rolesPromise]),
        timeoutPromise
      ]) as any;
      
      if (profileResult?.data) {
        organizationId = profileResult.data.organization_id;
        organizationName = profileResult.data.organization_name;
        isSuperAdmin = profileResult.data.is_super_admin || false;
        isDemo = profileResult.data.is_demo || false;
        console.log('[Auth] Fallback: Found organization:', organizationId);
        console.log('[Auth] Fallback: Super-admin status:', isSuperAdmin);
        console.log('[Auth] Fallback: Demo status:', isDemo);
      }
      
      if (rolesResult?.data && rolesResult.data.length > 0) {
        userRoles = rolesResult.data.map((r: any) => r.role as AppRole);
        isOrgAdmin = rolesResult.data.some((r: any) => r.is_organization_admin);
        console.log('[Auth] Fallback: Found roles from DB:', userRoles);
      }
    } catch (error) {
      console.warn('[Auth] Fallback: Could not fetch profile/roles:', error);
    }
    
    // If no roles from DB, try cache before defaulting to viewer
    if (!userRoles || userRoles.length === 0) {
      const cachedUser = authStateCache.getSnapshot();
      if (cachedUser?.roles && cachedUser.roles.length > 0) {
        userRoles = cachedUser.roles;
        console.log('[Auth] Fallback: Using cached user roles:', userRoles);
      } else {
        // Only default to viewer as last resort
        userRoles = ['viewer'];
        console.warn('[Auth] Fallback: No roles found, defaulting to viewer');
      }
    }
    
    // Calculate role priority
    const rolePriorities = { viewer: 1, cook: 2, manager: 3, admin: 4 };
    const highestRole = userRoles.reduce((highest, role) => 
      rolePriorities[role] > rolePriorities[highest] ? role : highest, 
      userRoles[0] || 'viewer'
    );
    
    // Create minimal profile from auth user data
    const fallbackProfile = {
      id: authUser.id,
      email: authUser.email!,
      first_name: authUser.user_metadata?.first_name || null,
      last_name: authUser.user_metadata?.last_name || null,
      organization_name: organizationName || authUser.user_metadata?.organization_name || null,
      organization_id: organizationId,
      avatar_url: null,
      created_at: authUser.created_at,
      updated_at: authUser.updated_at || authUser.created_at,
      is_super_admin: isSuperAdmin
    };

    console.log('[Auth] Fallback user created with roles:', userRoles, 'isOrgAdmin:', isOrgAdmin);

    return {
      id: authUser.id,
      email: authUser.email!,
      profile: fallbackProfile,
      roles: userRoles,
      highestRole: highestRole,
      rolePriority: rolePriorities[highestRole],
      isOrganizationAdmin: isOrgAdmin,
      organization: undefined,
      isDemo
    };
  },

  // Keep this method for backwards compatibility, but it's no longer needed
  // as the database trigger handles invitation processing automatically
  async processUserInvitation(userId: string, invitationId: string): Promise<void> {
    // This method is kept for backwards compatibility but is no longer used
    // The database trigger automatically processes invitations during user signup
  }
};
